import { Dialog, DialogActions, DialogContent } from '@material-ui/core'
import ButtonShadow from 'components/ButtonShadow/ButtonShadow'
import React, { useCallback, useMemo } from 'react'
import * as Styled from './ExclusiveContentModal.styled'
import { useTypedSelector } from 'reducers'
import { FullSupportInformation, SupportTier } from 'types'
import {
  createPathWithQueryParam,
  useGoToRoute,
  usePathName,
} from 'Routes/RouteAux'
import { getLoginPath, getPaymentsPath } from 'Routes/RouteNames'

function getTierLabel(tier?: SupportTier): string {
  return `"${tier.title ?? ''}" (R$ ${tier.value ?? ''})`
}

interface ExclusiveContentModalProps {
  isOpen: boolean
  fullSupportInformation: FullSupportInformation
  minSupportTierId: string | null
  supportTiersIds: string[] | null
  handleClose: () => void
}

const ExclusiveContentModal: React.FC<ExclusiveContentModalProps> = ({
  isOpen,
  fullSupportInformation,
  minSupportTierId,
  supportTiersIds,
  handleClose,
}) => {
  const tiers = fullSupportInformation.supportTiers
  const currentProfile = useTypedSelector(
    (state) => state && state.profiles && state.profiles.currentProfile,
  )

  const goToRoute = useGoToRoute()
  const pathName = usePathName()
  const loginRoute = createPathWithQueryParam(getLoginPath(), {
    redirectTo: pathName,
  })

  const tierInfoText = useMemo(() => {
    if (!currentProfile) {
      return undefined
    }

    if (minSupportTierId) {
      const minSupportTier = tiers.find((tier) => tier.id === minSupportTierId)
      return `Para acessar o conteúdo você deve ser apoiador a partir do nível ${getTierLabel(
        minSupportTier,
      )}.`
    }

    if (supportTiersIds) {
      const supportTiers = tiers.filter((tier) =>
        supportTiersIds.includes(tier.id),
      )
      const supportTiersLabel = supportTiers.map(getTierLabel)
      return `Para acessar o conteúdo você deve ser apoiador com algum dos seguintes níveis: ${supportTiersLabel.join(
        ', ',
      )}.`
    }

    return undefined
  }, [currentProfile, minSupportTierId, supportTiersIds, tiers])

  const handleGoTo = useCallback(() => {
    if (currentProfile) {
      goToRoute(
        getPaymentsPath(
          fullSupportInformation.supportSummary.podcastId,
          supportTiersIds?.[0] ??
            minSupportTierId ??
            fullSupportInformation.supportTiers[0].id,
        ),
      )
      return
    }

    goToRoute(loginRoute)
  }, [
    currentProfile,
    fullSupportInformation.supportSummary.podcastId,
    fullSupportInformation.supportTiers,
    goToRoute,
    loginRoute,
    minSupportTierId,
    supportTiersIds,
  ])

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogContent>
        <Styled.Container>
          <Styled.Title>
            {currentProfile
              ? 'Conteúdo exclusivo para apoiadores'
              : 'Você precisa estar logado'}
          </Styled.Title>
          {tierInfoText && <Styled.Info>{tierInfoText}</Styled.Info>}
        </Styled.Container>
      </DialogContent>
      <DialogActions style={{ gap: 20, padding: 20 }}>
        <ButtonShadow
          size="regular"
          variant="secondary"
          label="ok"
          onPress={handleClose}
        />
        <ButtonShadow
          size="regular"
          label={currentProfile ? 'Apoiar podcast' : 'Login'}
          onPress={handleGoTo}
        />
      </DialogActions>
    </Dialog>
  )
}

export default ExclusiveContentModal
